import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, Instagram, Clock, Sparkles } from 'lucide-react';
import { Button, Card, CardContent, Input, Select, Textarea } from '@leoclinic/ui';
import {
  usePublicTreatments,
  usePublicTherapists,
  useOnlineBooking,
  useAvailableSlots,
  useAppointmentSettings,
} from '@leoclinic/hooks';
import { BRAND_COLORS } from '@leoclinic/types';
import { useClinicPortal } from '@/providers/clinic-provider';
import { AuthNavLinks } from '@/components/auth-nav-links';
import { ClinicLogo } from '@/components/branding/clinic-logo';

const LISBON = 'Europe/Lisbon';

function slotDateKey(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: LISBON });
}

function formatDateLabel(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const weekday = date.toLocaleDateString('pt-PT', { weekday: 'short' });
  const formatted = date.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return `${weekday}, ${formatted}`;
}

function formatTimeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-PT', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: LISBON,
  });
}

function phoneDigits(phone: string | null | undefined): string {
  return phone?.replace(/\D/g, '') ?? '';
}

export default function BookingPage() {
  const navigate = useNavigate();
  const { clinic, clinicId, isLoading: clinicLoading } = useClinicPortal();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [form, setForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    treatment_id: '',
    therapist_id: '',
    appointment_date: '',
    start_at: '',
    notes: '',
  });

  const { data: treatments } = usePublicTreatments(clinicId);
  const { data: therapists } = usePublicTherapists(clinicId);
  const { data: appointmentSettings } = useAppointmentSettings(clinicId);
  const maxAdvanceDays = appointmentSettings?.max_advance_days ?? 60;

  const slotsReady = !!form.treatment_id && !!form.therapist_id;
  const { data: availableSlots = [], isLoading: slotsLoading } = useAvailableSlots(
    clinicId,
    form.therapist_id,
    form.treatment_id,
    maxAdvanceDays,
  );

  const booking = useOnlineBooking(clinicId);

  const highlights = clinic?.profile.highlights ?? [];
  const whatsappUrl = clinic?.profile.whatsapp
    ? `https://wa.me/${phoneDigits(clinic.profile.whatsapp)}`
    : clinic?.phone
      ? `https://wa.me/${phoneDigits(clinic.phone)}`
      : null;

  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    for (const slot of availableSlots) {
      dates.add(slotDateKey(slot.start_at));
    }
    return [...dates].sort();
  }, [availableSlots]);

  const timeOptions = useMemo(() => {
    if (!form.appointment_date) return [];
    return availableSlots
      .filter((slot) => slotDateKey(slot.start_at) === form.appointment_date)
      .map((slot) => ({
        value: slot.start_at,
        label: formatTimeLabel(slot.start_at),
      }));
  }, [availableSlots, form.appointment_date]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, appointment_date: '', start_at: '' }));
  }, [form.treatment_id, form.therapist_id]);

  useEffect(() => {
    if (!form.appointment_date) {
      setForm((prev) => (prev.start_at ? { ...prev, start_at: '' } : prev));
      return;
    }
    if (form.start_at && slotDateKey(form.start_at) !== form.appointment_date) {
      setForm((prev) => ({ ...prev, start_at: '' }));
    }
  }, [form.appointment_date, form.start_at]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clinicId || !form.start_at) return;
    setSubmitError(null);

    try {
      await booking.mutateAsync({
        client_name: form.client_name,
        client_email: form.client_email,
        client_phone: form.client_phone,
        treatment_id: form.treatment_id,
        therapist_id: form.therapist_id,
        start_at: form.start_at,
        notes: form.notes,
      });
      navigate('/confirmacao');
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Não foi possível enviar a marcação. Tente novamente ou contacte-nos por telefone.';
      setSubmitError(message);
    }
  };

  if (clinicLoading) {
    return (
      <div className="min-h-screen bg-[#F6F3EE] flex items-center justify-center p-4">
        <p className="text-muted-foreground">A carregar...</p>
      </div>
    );
  }

  if (!clinic) {
    return (
      <div className="min-h-screen bg-[#F6F3EE] flex items-center justify-center p-4 text-center">
        <p className="text-muted-foreground max-w-sm">
          Clínica não encontrada. Verifique <span className="font-mono">VITE_CLINIC_SLUG</span> e a configuração Supabase.
        </p>
      </div>
    );
  }

  const clinicName = clinic.name;
  const tagline = clinic.profile.tagline ?? 'Marcação online';

  return (
    <div className="min-h-screen bg-[#F6F3EE]">
      <header className="border-b border-[#ECECEC] bg-white">
        <div className="mx-auto max-w-lg px-4 py-6 text-center relative">
          <div className="absolute right-4 top-4 flex gap-2 text-xs">
            <AuthNavLinks />
          </div>
          <ClinicLogo size="lg" className="mx-auto mb-3" />
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: BRAND_COLORS.greenDark }}>
            {clinicName}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">{tagline}</p>
        </div>
      </header>

      <main className="mx-auto max-w-lg p-4 py-6 space-y-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-[#ECECEC]">
            <CardContent className="pt-6 space-y-3 text-sm">
              {clinic.profile.description && (
                <p className="text-muted-foreground leading-relaxed">{clinic.profile.description}</p>
              )}
              {highlights.length > 0 && (
                <ul className="grid gap-1.5">
                  {highlights.map((h) => (
                    <li key={h} className="flex items-center gap-2 text-foreground/80">
                      <Sparkles className="h-3.5 w-3.5 shrink-0" style={{ color: BRAND_COLORS.gold }} />
                      {h}
                    </li>
                  ))}
                </ul>
              )}
              {(clinic.address || clinic.city) && (
                <div className="flex items-start gap-2 pt-2 text-muted-foreground">
                  <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    {[clinic.address, clinic.postal_code, clinic.city].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4 shrink-0" />
                <span>{clinic.hoursLabel}</span>
              </div>
              <div className="flex flex-wrap gap-3 pt-1">
                {clinic.phone && (
                  <a
                    href={`tel:${clinic.phone.replace(/\s/g, '')}`}
                    className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
                    style={{ color: BRAND_COLORS.greenDark }}
                  >
                    <Phone className="h-4 w-4" />
                    {clinic.phone}
                  </a>
                )}
                {whatsappUrl && (
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
                    style={{ color: BRAND_COLORS.greenDark }}
                  >
                    WhatsApp
                  </a>
                )}
                {clinic.profile.instagram_url && (
                  <a
                    href={clinic.profile.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
                    style={{ color: BRAND_COLORS.greenDark }}
                  >
                    <Instagram className="h-4 w-4" />
                    Instagram
                  </a>
                )}
                {clinic.profile.facebook_url && (
                  <a
                    href={clinic.profile.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
                    style={{ color: BRAND_COLORS.greenDark }}
                  >
                    Facebook
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-4" style={{ color: BRAND_COLORS.greenDark }}>
                Marcar sessão
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input label="Nome completo *" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required />
                <Input label="Email *" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} required />
                <Input label="Telefone *" placeholder="9XX XXX XXX" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} required />
                <Select
                  label="Tratamento *"
                  placeholder="Seleccionar"
                  options={(treatments ?? []).map((t: { id: string; name: string; duration_minutes: number; price?: number }) => ({
                    value: t.id,
                    label: `${t.name} (${t.duration_minutes} min${t.price ? ` · ${t.price}€` : ''})`,
                  }))}
                  value={form.treatment_id}
                  onChange={(e) => setForm({ ...form, treatment_id: e.target.value })}
                  required
                />
                <Select
                  label="Terapeuta *"
                  placeholder="Seleccionar"
                  options={(therapists ?? []).map((t: { id: string; full_name: string }) => ({
                    value: t.id,
                    label: t.full_name,
                  }))}
                  value={form.therapist_id}
                  onChange={(e) => setForm({ ...form, therapist_id: e.target.value })}
                  required
                />

                {!slotsReady ? (
                  <p className="text-sm text-muted-foreground rounded-lg border border-border bg-muted/30 px-3 py-2">
                    Escolha o tratamento e o terapeuta para ver as datas disponíveis.
                  </p>
                ) : slotsLoading ? (
                  <p className="text-sm text-muted-foreground">A carregar horários disponíveis...</p>
                ) : availableDates.length === 0 ? (
                  <p className="text-sm text-amber-800 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                    Sem horários disponíveis nos próximos {maxAdvanceDays} dias. Contacte-nos por telefone ou WhatsApp.
                  </p>
                ) : (
                  <>
                    <Select
                      label="Data *"
                      placeholder="Seleccionar data"
                      options={availableDates.map((date) => ({
                        value: date,
                        label: formatDateLabel(date),
                      }))}
                      value={form.appointment_date}
                      onChange={(e) => setForm({ ...form, appointment_date: e.target.value, start_at: '' })}
                      required
                    />
                    <Select
                      label="Hora *"
                      placeholder={form.appointment_date ? 'Seleccionar hora' : 'Escolha primeiro a data'}
                      options={timeOptions}
                      value={form.start_at}
                      onChange={(e) => setForm({ ...form, start_at: e.target.value })}
                      required
                      disabled={!form.appointment_date || timeOptions.length === 0}
                    />
                  </>
                )}

                <Textarea label="Notas (opcional)" rows={2} placeholder="Alguma preferência ou informação útil?" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                {submitError && (
                  <p className="text-sm text-destructive rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
                    {submitError}
                  </p>
                )}
                <Button
                  type="submit"
                  className="w-full"
                  disabled={booking.isPending || !clinicId || !form.start_at}
                >
                  {booking.isPending ? 'A enviar...' : 'Confirmar Marcação'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </main>

      <footer className="text-center text-xs text-muted-foreground pb-8 px-4">
        © {new Date().getFullYear()} {clinicName}
        {clinic.city ? ` · ${clinic.city}` : ''}
      </footer>
    </div>
  );
}
